import type { CareLogArea, MyGrower } from '@/lib/api/care-logs-api'
import type { FilterOption } from '@/components/common/filters/select-filter'
import { useAuth } from '@/hooks/use-auth'
import {
    useCareLogAreasOfGrowers,
    useMyGrowers,
} from '@/hooks/use-care-logs-query'

/**
 * Lựa chọn của ô lọc vùng trồng, gộp từ vùng của từng hộ (`areasByGrower` cùng
 * thứ tự với `growers`). Nhãn `mã — tên` giống ô chọn vùng của form nhật ký.
 * Gộp từ NHIỀU hộ thì thêm tên hộ vào nhãn: hai hộ đặt trùng tên vùng là chuyện
 * thường ("Bãi trên"), không có tên hộ thì người dùng không phân biệt được.
 */
export function careLogAreaFilterOptions(
    growers: Pick<MyGrower, 'id' | 'name'>[],
    areasByGrower: CareLogArea[][],
): FilterOption<string>[] {
    const withGrower = growers.length > 1
    const seen = new Set<string>()
    const out: FilterOption<string>[] = []
    growers.forEach((grower, i) => {
        for (const a of areasByGrower[i] ?? []) {
            if (seen.has(a.id)) continue
            seen.add(a.id)
            const base = a.code ? `${a.code} — ${a.name}` : a.name
            out.push({
                value: a.id,
                label: withGrower ? `${base} (${grower.name})` : base,
            })
        }
    })
    return out
}

export interface CareLogAreaFilter {
    options: FilterOption<string>[]
    /** Danh sách đã tải đủ cho đúng hộ đang lọc — chỉ khi đó mới được kết
     *  luận một `?area=` là không hợp lệ. Admin chưa chọn hộ → false. */
    isReady: boolean
}

/**
 * Vùng trồng cho bộ lọc màn danh sách nhật ký.
 *
 * - Admin: chỉ vùng của hộ ĐANG LỌC (`?grower=`) — không có endpoint trả vùng
 *   của mọi hộ, và màn ẩn ô vùng khi chưa chọn hộ.
 * - Tài khoản hộ: vùng của mọi hộ gắn với tài khoản (server đã giới hạn phạm vi).
 *
 * `grower` là giá trị `?grower=` của route, do màn truyền vào (qua
 * `Route.useSearch()`), KHÔNG tự đọc router ở đây: hook này còn chạy trong
 * control của `remoteSelectFilter`, và `useSearch` rời route ném lỗi khi cây
 * render không nằm trong RouterProvider (test màn, story).
 */
export function useCareLogAreaFilter(grower: unknown): CareLogAreaFilter {
    const { hasPermission } = useAuth()
    const isAdmin = hasPermission('care_logs.manage_all')
    const {
        data: myGrowers,
        isSuccess: growersLoaded,
        isFetching: growersFetching,
    } = useMyGrowers(!isAdmin)

    const adminGrower = typeof grower === 'string' && grower ? grower : ''
    const growers: Pick<MyGrower, 'id' | 'name'>[] = isAdmin
        ? adminGrower
            ? [{ id: adminGrower, name: '' }]
            : []
        : (myGrowers ?? [])
    const areas = useCareLogAreasOfGrowers(growers.map((g) => g.id))
    return {
        options: careLogAreaFilterOptions(growers, areas.lists),
        // `useQueries` rỗng coi là xong, nên phải chặn thêm ở tầng hộ: admin
        // chưa chọn hộ, hoặc danh sách hộ của tài khoản chưa về. Đang tải lại
        // cũng chưa tính: cache cũ có thể thiếu vùng vừa thêm → xoá nhầm.
        isReady:
            areas.isSuccess &&
            !areas.isFetching &&
            (isAdmin ? !!adminGrower : growersLoaded && !growersFetching),
    }
}

/** Dạng `useOptions` mà `remoteSelectFilter` cần. */
export function useCareLogAreaFilterOptions(
    grower: unknown,
): FilterOption<string>[] {
    return useCareLogAreaFilter(grower).options
}
